// src/components/TaskSplitDetail.tsx
// Compact task detail panel designed for the split view context.
// Thin view layer — data loaded from store, no direct API calls.

import { useEffect, useCallback, useRef, useState } from 'react';
import { observer } from '@react';
import { useTask, useTaskStatusDefinition, useI18n } from '@react/hooks';
import { ErrorAlert } from '@react/components';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { cn } from '@core/ui';
import { entityStore } from '@/stores/EntityStore';
import { PropertiesPanel } from '@/components/PropertiesPanel';
import { StatusSelect } from '@/components/StatusSelect';
import { PrioritySelect } from '@/components/PrioritySelect';
import {
  formatRelativeTime,
  buildStatusChangeRequest,
  buildTaskFieldUpdate,
  getTaskStatus,
  getTaskPriority,
  getDisplayName,
  formatActivityType,
} from '@/features/task/logic';

import type { Task } from '@core/types/task';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface TaskSplitDetailProps {
  taskId: string;
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  /** Notifies the host list so the row can reflect an edit made here. */
  onUpdated?: (taskId: string, patch: Record<string, unknown>) => void;
}

// ---------------------------------------------------------------------------
// Inline icons
// ---------------------------------------------------------------------------

function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ChevronLeftIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="15 18 9 12 15 6" />
    </svg>
  );
}

function ChevronRightIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round">
      <polyline points="9 18 15 12 9 6" />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const TaskSplitDetail = observer(function TaskSplitDetail({
  taskId,
  onClose,
  onNavigate,
  onUpdated,
}: TaskSplitDetailProps) {
  const store = useTask();
  const statusDefStore = useTaskStatusDefinition();
  const { t } = useI18n();

  const [activities, setActivities] = useState<any[]>([]);
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [editTitle, setEditTitle] = useState('');
  const titleInputRef = useRef<HTMLInputElement>(null);

  // The `s` / `p` shortcuts open the pickers by clicking their triggers.
  // StatusSelect / PrioritySelect own their open state, so the wrapper is the
  // only handle available without changing those shared components.
  const statusPickerRef = useRef<HTMLDivElement>(null);
  const priorityPickerRef = useRef<HTMLDivElement>(null);

  const item = store.currentItem as any;

  // Custom statuses drive the status picker; the workspace usually already
  // loaded them, so only fetch when this panel is used stand-alone.
  useEffect(() => {
    if (statusDefStore.items.length === 0) {
      statusDefStore.ListStatusDefinitions();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Load task data ────────────────────────────────────────────────────
  useEffect(() => {
    if (taskId) {
      store.GetTask(taskId);
      store.ListActivity?.(taskId)
        ?.then((res: any) => setActivities((res?.activities || res?.items || []).slice(0, 5)))
        ?.catch(() => {});
    }

    return () => {
      store.clearCurrent();
      entityStore.clearContext();
    };
  }, [taskId]);

  // ── Load entity context for Right Rail ────────────────────────────────
  useEffect(() => {
    if (item?.entity) {
      entityStore.loadEntityData({
        urn: item.entity.urn || '',
        type: item.entity.type_name || 'task',
        id: item.entity.id || taskId,
        title: item.title || '',
      });
    }
  }, [item?.entity?.id]);

  // ── Keyboard shortcuts ────────────────────────────────────────────────
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      // Skip when user is typing in an input
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      // Leave browser/OS chords alone (⌘S, Ctrl+P, ...)
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      switch (e.key) {
        case '[':
          e.preventDefault();
          onNavigate?.('prev');
          break;
        case ']':
          e.preventDefault();
          onNavigate?.('next');
          break;
        case 's':
        case 'S':
          e.preventDefault();
          statusPickerRef.current?.querySelector('button')?.click();
          break;
        case 'p':
        case 'P':
          e.preventDefault();
          priorityPickerRef.current?.querySelector('button')?.click();
          break;
        default:
          break;
      }
    },
    [onNavigate],
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // ── Status change handler ─────────────────────────────────────────────
  const handleStatusChange = useCallback(
    async (newStatus: string) => {
      if (!taskId) return;
      const request = buildStatusChangeRequest(taskId, newStatus, statusDefStore.items);
      try {
        await store.ChangeTaskStatus(taskId, request);
        onUpdated?.(taskId, { status: request.status, custom_status: request.custom_status || '' });
        store.GetTask(taskId);
      } catch {
        /* store handles error */
      }
    },
    [store, statusDefStore.items, taskId, onUpdated],
  );

  // ── Priority change handler ───────────────────────────────────────────
  const handlePriorityChange = useCallback(
    async (newPriority: string) => {
      if (!taskId) return;
      try {
        await store.UpdateTask(
          taskId,
          buildTaskFieldUpdate(taskId, { priority: newPriority as Task['priority'] }),
        );
        onUpdated?.(taskId, { priority: newPriority });
        store.GetTask(taskId);
      } catch {
        /* store handles error */
      }
    },
    [store, taskId, onUpdated],
  );

  // ── Title editing ─────────────────────────────────────────────────────
  const startEditTitle = useCallback(() => {
    setEditTitle(item?.title || '');
    setIsEditingTitle(true);
  }, [item?.title]);

  useEffect(() => {
    if (isEditingTitle) titleInputRef.current?.focus();
  }, [isEditingTitle]);

  const commitTitle = useCallback(async () => {
    setIsEditingTitle(false);
    const title = editTitle.trim();
    if (!title || title === item?.title) return;
    try {
      await store.UpdateTask(taskId, buildTaskFieldUpdate(taskId, { title }));
      onUpdated?.(taskId, { title });
      store.GetTask(taskId);
    } catch {
      /* store handles error */
    }
  }, [editTitle, item?.title, store, taskId, onUpdated]);

  // ── Loading state ─────────────────────────────────────────────────────
  if (store.isLoading || store.isLoadingItem) {
    return (
      <div className="p-4 space-y-4 animate-pulse">
        <div className="flex items-center gap-2">
          <div className="h-4 w-4 rounded bg-muted" />
          <div className="h-4 w-20 rounded bg-muted" />
        </div>
        <div className="h-6 w-3/4 rounded bg-muted" />
        <div className="h-4 w-1/2 rounded bg-muted" />
        <div className="space-y-2 mt-6">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="flex justify-between">
              <div className="h-4 w-24 rounded bg-muted" />
              <div className="h-4 w-32 rounded bg-muted" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────
  if (store.error && !item) {
    return (
      <div className="p-4">
        <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} />
      </div>
    );
  }

  // ── Not found ─────────────────────────────────────────────────────────
  if (!item) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        <p>{t('common.notFound')}</p>
      </div>
    );
  }

  const task = item.task || {};
  const status = getTaskStatus(item);
  const assignee = task.assignee;

  // ── Render ────────────────────────────────────────────────────────────
  return (
    <div className="flex h-full flex-col">
      {/* ── Header ──────────────────────────────────────────────────── */}
      <div className="flex items-center gap-1 border-b border-border px-3 py-2 shrink-0">
        {/* Prev / Next navigation */}
        {onNavigate && (
          <>
            <button
              type="button"
              onClick={() => onNavigate('prev')}
              className={cn(
                'inline-flex items-center justify-center rounded p-1',
                'text-muted-foreground hover:text-foreground hover:bg-muted',
                'transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              aria-label={t('common.previous')}
              title="["
            >
              <ChevronLeftIcon className="h-4 w-4" />
            </button>
            <button
              type="button"
              onClick={() => onNavigate('next')}
              className={cn(
                'inline-flex items-center justify-center rounded p-1',
                'text-muted-foreground hover:text-foreground hover:bg-muted',
                'transition-colors',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              )}
              aria-label={t('common.next')}
              title="]"
            >
              <ChevronRightIcon className="h-4 w-4" />
            </button>
          </>
        )}

        {/* Task key */}
        {item.key && (
          <span className="ml-1 text-xs font-mono text-muted-foreground">{item.key}</span>
        )}

        {/* Spacer */}
        <div className="flex-1" />

        {/* Close */}
        <button
          type="button"
          onClick={onClose}
          className={cn(
            'inline-flex items-center justify-center rounded p-1',
            'text-muted-foreground hover:text-foreground hover:bg-muted',
            'transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          )}
          aria-label={t('common.close')}
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      {/* ── Scrollable body ─────────────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 space-y-4">
          {/* Title + status */}
          <div className="space-y-1.5">
            {isEditingTitle ? (
              <input
                ref={titleInputRef}
                type="text"
                value={editTitle}
                aria-label={t('task.field.title')}
                onChange={(e) => setEditTitle(e.target.value)}
                onBlur={commitTitle}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitTitle();
                  if (e.key === 'Escape') setIsEditingTitle(false);
                }}
                className={cn(
                  'w-full text-lg font-semibold text-foreground bg-transparent',
                  'border-b-2 border-primary outline-none',
                )}
              />
            ) : (
              <h2
                className="text-lg font-semibold text-foreground cursor-pointer hover:text-primary transition-colors"
                onDoubleClick={startEditTitle}
                title={t('task.doubleClickToEdit')}
              >
                {String(item.title ?? '')}
              </h2>
            )}

            {/* Status / priority pickers — also reachable with `s` and `p` */}
            <div className="flex items-center gap-2 pt-0.5">
              <div ref={statusPickerRef} title={t('task.shortcut.status')}>
                <StatusSelect value={status || ''} onSelect={handleStatusChange} />
              </div>
              <div ref={priorityPickerRef} title={t('task.shortcut.priority')}>
                <PrioritySelect value={getTaskPriority(item) || ''} onSelect={handlePriorityChange} />
              </div>
            </div>
          </div>

          {/* ── Properties ──────────────────────────────────────────── */}
          <PropertiesPanel
            status={status}
            priority={task.priority}
            assignee={
              assignee
                ? { id: assignee.id || '', display_name: getDisplayName(assignee) }
                : null
            }
            dueDate={task.due_date}
            type={item.type}
            labels={task.tags}
            progress={task.progress_percent}
            timeTracked={task.actual_duration}
            timeEstimated={task.estimated_duration}
            createdAt={item.created_at}
            createdBy={getDisplayName(item.creator)}
            updatedAt={item.updated_at}
            parentKey={item.parent?.urn || item.parent?.id}
            watchers={task.watchers?.actors?.map((w: any) => ({
              id: w.id || '',
              display_name: getDisplayName(w),
            }))}
            onStatusChange={handleStatusChange}
            className="text-sm"
          />

          {/* ── Description ─────────────────────────────────────────── */}
          {item.description && (
            <div className="space-y-1">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('task.field.description')}
              </h3>
              <div className="prose prose-sm dark:prose-invert max-w-none text-foreground">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {String(item.description)}
                </ReactMarkdown>
              </div>
            </div>
          )}

          {/* ── Recent Activity ─────────────────────────────────────── */}
          {activities.length > 0 && (
            <div className="space-y-2">
              <h3 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                {t('task.activity')}
              </h3>
              <div className="space-y-1.5">
                {activities.map((activity: any, idx: number) => (
                  <div
                    key={activity.entity?.id || idx}
                    className="flex items-start gap-2 text-sm rounded px-2 py-1.5 hover:bg-muted/50 transition-colors"
                  >
                    <div className="flex-1 min-w-0">
                      <span className="font-medium text-foreground">
                        {getDisplayName(activity.actor)}
                      </span>{' '}
                      <span className="text-muted-foreground">
                        {activity.activity_type
                          ? formatActivityType(String(activity.activity_type))
                          : t('task.update')}
                      </span>
                      {activity.field && (
                        <span className="text-muted-foreground">
                          {' '}{activity.field}
                        </span>
                      )}
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap shrink-0">
                      {formatRelativeTime(activity.created_at)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
});

export default TaskSplitDetail;
